/* MLS-SVC-DIR-001 — frontend mirror of backend MLS_SERVICE_TYPES (mls section → service type) */
export const SECTION_SERVICE_TYPE = {
  code_violations: 'code_enforcement',
  open_permits: 'permits',
  utility_accounts: 'water_sewer',
  trash: 'trash',
};

/* inverse of SECTION_SERVICE_TYPE — storage section for a service type (MLS-SVC-REQ-003 origin) */
export const SERVICE_SECTION = Object.fromEntries(Object.entries(SECTION_SERVICE_TYPE).map(([k, v]) => [v, k]));

export const SERVICE_LABELS = {
  code_enforcement: 'Code Enforcement',
  permits: 'Building Permits',
  water_sewer: 'Water/Sewer',
  trash: 'Trash',
};

export const LEVEL_LABEL = { municipality: 'City', county: 'County', other: 'Other' };

export const levelLabel = (entity) => {
  const t = (entity?.jurisdiction_type || '').toLowerCase().replace('_', ' ');
  if (entity?.jurisdiction_level === 'county') return 'County';
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : 'City';
};

export const entityPhone = (e) => (e?.phone ? `${e.phone}${e.phone_ext ? ` ext. ${e.phone_ext}` : ''}` : '');

/* MLS-UTILITY-001: Utility is ONE nav section over two storage sections (utility_accounts + trash).
   `utility_type` is a per-entry key; legacy entries derive it (never migrated). */
/* MLS-SEARCH-FINDINGS-ENHANCEMENT-001: "Other Utility" retired for NEW selections — legacy entries stay readable */
export const UTILITY_TYPES = [['water', 'Water'], ['sewer', 'Sewer'], ['trash', 'Trash']];
/* MLS-UTILITY-TYPE-DROPDOWN-001: Water/Sewer is a first-class, always-listed choice (no "—" placeholder on a required field) */
export const UTILITY_TYPE_OPTIONS = [['water_sewer', 'Water/Sewer'], ...UTILITY_TYPES];
export const UTILITY_LEGACY_TYPE_OPTION = { other: ['other', 'Other Utility (legacy)'] };
export const UTILITY_TYPE_LABEL = { water: 'Water', sewer: 'Sewer', water_sewer: 'Water/Sewer', trash: 'Trash', other: 'Other Utility' };
export const UTILITY_TAB_LABEL = { water: 'Water', sewer: 'Sewer', water_sewer: 'Water/Sewer', trash: 'Trash', other: 'Utility' };
export const utilityTypeOf = (entry, src) => {
  const t = (entry?.utility_type || '').toLowerCase();
  if (UTILITY_TYPE_LABEL[t]) return t;
  if (src === 'trash') return 'trash';
  return { water: 'water', sewer: 'sewer' }[(entry?.type || '').trim().toLowerCase()] || 'water_sewer';
};
export const utilitySrcForType = (t) => (t === 'trash' ? 'trash' : 'utility_accounts');

/* MLS-UTILITY-WELL-SEPTIC-001: per-entry `utility_source` — Water: public_water | private_well, Sewer: public_sewer | septic_tank.
   Legacy entries have no value (public workflow unchanged). Mirror of backend/services/mls_auto_findings.py. */
export const UTILITY_SOURCE_OPTIONS = {
  water: [['public_water', 'Public Water'], ['private_well', 'Private Well']],
  sewer: [['public_sewer', 'Public Sewer'], ['septic_tank', 'Septic Tank']],
};
export const UTILITY_SOURCE_LABEL = { public_water: 'Public Water', private_well: 'Private Well', public_sewer: 'Public Sewer', septic_tank: 'Septic Tank' };
export const UTILITY_SOURCE_FIELD_LABEL = { water: 'Water Source', sewer: 'Sewer Type' };
export const UTILITY_SOURCE_NOTE = {
  private_well: 'The property is served by a private well. No public water connection is reported.',
  septic_tank: 'The property is served by a septic tank. No public sewer connection is reported.',
};
export const isOffGridSource = (v) => v === 'private_well' || v === 'septic_tank';
/* MLS-UTILITY-WELL-SEPTIC-002: the selection is mandatory on every Water / Sewer entry (never pre-selected) */
export const sourceRequiredMessage = (utype) => (utype === 'water' ? 'Water Source required' : 'Sewer Type required');
export const sourceMissing = (entry, utype) => !!UTILITY_SOURCE_OPTIONS[utype] && !entry?.utility_source;
export const entryOffGrid = (entry) => isOffGridSource(entry?.utility_source);
/* MLS-UTILITY-FINDINGS-GATE-001: Search Findings prerequisites (mirror of backend utility_findings_gate).
   'source' → Water Source / Sewer Type not chosen; 'entity' → public entry without jurisdiction/entity; null → open.
   Private Well / Septic Tank are exempt (off-grid rule). */
export const UTILITY_FINDINGS_GATE_MSG = 'Complete the utility source and entity selection to enter search findings.';
export const utilityFindingsGate = (entry, utype, entityField) => {
  const src = (entry?.utility_source || '').trim();
  if (UTILITY_SOURCE_OPTIONS[utype] && !src) return 'source';
  if (isOffGridSource(src)) return null;
  if (!(entry?.[entityField] || '').trim()) return 'entity';
  return null;
};
/* keep `utility_source` consistent with the entry's utility type (a Water value is meaningless on a Sewer entry) */
export const sourceValidFor = (utype, v) => (UTILITY_SOURCE_OPTIONS[utype] || []).some(([k]) => k === v);
/* strip exactly the auto-generated sentence from processor notes — everything else is kept verbatim */
export const stripSourceNote = (notes, sentence) => {
  const t = String(notes || '');
  if (!sentence || !t.includes(sentence)) return t;
  return t.replace(sentence, '').replace(/^\s*\n/, '').replace(/\n\s*$/, '').trim();
};
export const UTILITY_ENTITY_FIELD = { utility_accounts: 'water_sewer_collection_entity', trash: 'trash_collection_entity' };

/* MLS-SVC-UX-001: manual-entry authority level (same `service_level` key the directory snapshot uses) */
export const MANUAL_LEVELS = [['municipality', 'City'], ['county', 'County'], ['other', 'Other']];
export const entryLevelLabel = (entry) => LEVEL_LABEL[(entry?.service_level || '').toLowerCase()] || '';
export const LINK_KEYS = ['service_entity_id', 'service_type', 'service_jurisdiction_id', 'service_entity_snapshot_at'];

export const feeText = (amount, note) => {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = Number(amount);
  const txt = Number.isFinite(n) ? `$${n.toFixed(2)}` : String(amount);
  return note ? `${txt} — ${note}` : txt;
};
export const SNAPSHOT_KEYS = ['address', 'phone_number', 'phone_ext', 'contact', 'fax', 'email', 'website', 'fee_amount', 'fee_note'];

/* Snapshot fields written onto an MLS entry when an entity is applied */
export const applyEntityToEntry = (entry, def, entity) => ({
  ...entry,
  [def.entityField]: entity.entity_name || '',
  address: entity.physical_address || '',
  phone_number: entity.phone || '',
  phone_ext: entity.phone_ext || '',
  contact: entity.contact_name || '',
  fax: entity.fax || '',
  email: entity.email || '',
  website: entity.website || '',
  fee_amount: entity.fee_amount === null || entity.fee_amount === undefined ? '' : Number(entity.fee_amount).toFixed(2),
  fee_note: entity.fee_note || '',
  service_entity_id: entity.id,
  service_type: entity.service_type,
  service_jurisdiction_id: entity.jurisdiction_id,
  service_jurisdiction_name: entity.jurisdiction_name,
  service_level: entity.jurisdiction_level,
  service_entity_snapshot_at: new Date().toISOString(),
});

export const entryContactEmpty = (entry, def) =>
  !entry?.service_entity_id &&
  ![entry?.[def.entityField], ...SNAPSHOT_KEYS.map((k) => entry?.[k])].some((v) => String(v || '').trim());

/* Fill empty entries for every section whose resolution mode is `auto`. Pure. */
export const withAutoEntities = (form, resolution, defs) => {
  if (!resolution?.services) return form;
  let next = form;
  defs.forEach((def) => {
    const svc = resolution.services[SECTION_SERVICE_TYPE[def.key]];
    if (svc?.mode !== 'auto' || !svc.auto) return;
    const sec = next[def.key] || { none_found: false, entries: [] };
    if (sec.none_found) return;
    const entries = sec.entries?.length ? sec.entries : [{}];
    // an added entry is never pre-filled with an entity another entry in the section already uses
    if (entries.some((e) => e?.service_entity_id === svc.auto.id)) return;
    let changed = false;
    const filled = entries.map((e) => {
      if (!entryContactEmpty(e, def)) return e;
      changed = true;
      return applyEntityToEntry(e, def, svc.auto);
    });
    if (changed) next = { ...next, [def.key]: { ...sec, entries: filled } };
  });
  return next;
};
